import { useCallback } from 'react';
import { makeApiCall } from '../../../../utils/api';
import { API_PATHS } from '../../../../config/api-endpoints';
import { ChatMessage, CreateJobRequest } from '../types';

interface UseJobCreationProps {
    profileId: string;
    setLocalMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
    setWsUiState: React.Dispatch<React.SetStateAction<any>>;
}

/**
 * Hook for job creation functionality
 */
export const useJobCreation = ({
    profileId,
    setLocalMessages,
    setWsUiState
}: UseJobCreationProps) => {
    
    // Create job via API
    const createJob = useCallback(async (jobName: string) => {
        try {
            // Get jobId from template generation (this is what CreateJob expects)
            const jobId = window.__ADDAPT_TEMPLATE__?.jobId;
            if (!jobId) {
                throw new Error('No job ID found. Template must be generated first.');
            }
            
            const request: CreateJobRequest = {
                jobId,  // Job ID from GenerateTemplate response
                jobName,
                // Optional fields can be added here
                // ldap: profileData?.ldap,
                // description: `Job for ${profileName}`,
                // jobType: 'RELEASE'
            };
            
            // Call API
            const response = await makeApiCall(API_PATHS.JOBS.CREATE, 'POST', request);
            
            // Show success message
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `job-created-${Date.now()}`,
                type: 'assistant',
                content: `🎉 Job "${jobName}" created successfully!\n\nYou can now run this job from the Jobs page.`,
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Show action buttons
            setWsUiState((prev: any) => ({
                ...prev,
                input_enabled: false,
                chips: [
                    {
                        id: 'view_jobs',
                        text: '📋 View Jobs',
                        action: 'navigate',
                        value: `/profile/${profileId}/jobs`
                    }
                ]
            }));
        } catch (error: any) {
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `error-${Date.now()}`,
                type: 'system',
                content: `❌ Failed to create job: ${error.message}`,
                timestamp: new Date(),
                status: 'sent'
            }]);
        }
    }, [profileId, setLocalMessages, setWsUiState]);

    return {
        createJob
    };
};

